import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
  UploadOutlined,
  Visibility,
} from "@mui/icons-material";
import {
  DialogTypes,
  KOMATSU_MANUFACTURER_ID,
  OTHER_MACHINE_TYPE_ID,
  OTHER_MANUFACTURER_ID,
  TYPE_FILE_UPLOAD,
} from "@app/constants";
import { useTranslation } from "react-i18next";
import {
  IconDelete,
  IconDeleteRow,
  IconInputError,
} from "@app/components/icons";
import { ConfirmDialog } from "../Components/InspectionForm/ConfirmDialog";
import { MediaUploadMachine } from "../Components/InspectionForm/MediaUpload/MediaUploadMachine";
import { REGEX, isValidMediaFile } from "@app/helpers";
import heic2any from "heic2any";
import { DataDialog } from "@app/components/atoms";
import { ImageCropDialog } from "../Components/InspectionForm/MediaUpload/ImageCropDialog";

interface MachineCardProps {
  cardId: string;
  index: number;
  defaultOpen?: boolean;
  manufacturers: any[];
  machinetype: any[];
  machineName: string;
  onMachineNameChange: (machineName: string) => void;
  machineManufacturerId: string;
  onMachineManufacturerIdChange: (machineManufacturerId: string) => void;
  customMachineManufacturerName: string;
  oncustomMachineManufacturerNameChange: (
    customMachineManufacturerName: string,
  ) => void;
  machineTypeId: string;
  onmachineTypeIdChange: (machineTypeId: string) => void;
  customTypeName: string;
  oncustomTypeNameChange: (customTypeName: string) => void;
  modelAndType: string;
  onmodelAndTypeChange: (modelAndType: string) => void;
  serialNumber: string;
  onserialNumberChange: (serialNumber: string) => void;
  onDelete: (id: string) => void;
  pictureUrl: string | null;
  pictureUrlChange: (pictureUrl: string | null) => void;
  serialNumberPlatePictureUrl: File[];
  onserialNumberPlatePictureUrlChange: (
    serialNumberPlatePictureUrl: File[],
  ) => void;
  setCardOpenClose: React.Dispatch<React.SetStateAction<any[]>>;
  onErrorChange: (cardId: string, field: string, hasError: boolean) => void;
  setPictureFiles: (setPictureFiles: File[]) => void;
  isCopied: boolean;
}

const MachineCard: React.FC<MachineCardProps> = ({
  cardId,
  index,
  defaultOpen = false,
  manufacturers,
  machinetype,
  machineName,
  onMachineNameChange,
  machineManufacturerId,
  onMachineManufacturerIdChange,
  customMachineManufacturerName,
  oncustomMachineManufacturerNameChange,
  machineTypeId,
  onmachineTypeIdChange,
  customTypeName,
  oncustomTypeNameChange,
  modelAndType,
  onmodelAndTypeChange,
  serialNumber,
  onserialNumberChange,
  onDelete,
  pictureUrl,
  pictureUrlChange,
  serialNumberPlatePictureUrl,
  onserialNumberPlatePictureUrlChange,
  setCardOpenClose,
  onErrorChange,
  setPictureFiles,
  isCopied,
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = React.useState(false);
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [, setIsImageRemoved] = useState(false);
  const [fileError, setFileError] = useState<null | string>(null);
  const [machineNumberError, setMachineNumberError] = useState<string | null>(
    null,
  );
  const [makerOtherError, setMakerOtherError] = useState<string | null>(null);
  const [machineTypeOtherError, setMachineTypeOtherError] = useState<
    string | null
  >(null);
  const [modelTypeError, setModelTypeError] = useState<string | null>(null);
  const [forbiddenCharError, setForbiddenCharError] = useState(false);
  const [serialNoError, setSerialNoError] = useState(false);

  const [refreshKey] = useState(0);
  const [, setFileCount] = useState(0);
  const [, setRemovedMediaUrls] = useState<string[]>([]);

  // Local state for input values
  const [localMachineName, setLocalMachineName] = useState(machineName);
  const [
    localCustomMachineManufacturerName,
    setLocalCustomMachineManufacturerName,
  ] = useState(customMachineManufacturerName);
  const [localCustomTypeName, setLocalCustomTypeName] =
    useState(customTypeName);
  const [localModelAndType, setLocalModelAndType] = useState(modelAndType);
  const [localSerialNumber, setLocalSerialNumber] = useState(serialNumber);

  // Sync local state with props when they change externally
  useEffect(() => {
    setLocalMachineName(machineName);
  }, [machineName]);

  useEffect(() => {
    setLocalCustomMachineManufacturerName(customMachineManufacturerName);
  }, [customMachineManufacturerName]);

  useEffect(() => {
    setLocalCustomTypeName(customTypeName);
  }, [customTypeName]);

  useEffect(() => {
    setLocalModelAndType(modelAndType);
  }, [modelAndType]);

  useEffect(() => {
    setLocalSerialNumber(serialNumber);
  }, [serialNumber]);

  const uploadDescription = [
    `${t("textDesUploadFile.pleaseUploadAFileShowingTheMachinePoints")}`,
    `${t("textDesUploadFile.fileFormatsThatCanBeUploadedAre")}`,
  ];

  const confirmProps = {
    typeDialog: DialogTypes.ERROR,
    title: "titleDeleteCard",
    content: "desDeleteCard",
    btnSubmitText: "formAction.delete",
    onCancel() {
      setIsOpenDeleteDialog(false);
    },
    onSubmit() {
      onDelete(cardId);
      setIsOpenDeleteDialog(false);
    },
    classBtnSubmit: "btn-primary bg-red",
  };

  const toggleOpen = () => {
    setIsOpen((prev) => {
      const newIsOpen = !prev;

      setCardOpenClose((prevCards: any[]) =>
        prevCards.map((card) =>
          card.cardId === cardId ? { ...card, isOpen: newIsOpen } : card,
        ),
      );

      return newIsOpen;
    });
  };

  const onErrorChangeRef = useRef(onErrorChange);

  useEffect(() => {
    onErrorChangeRef.current = onErrorChange;
  }, [onErrorChange]);

  const handleMachineNumberChange = (value: string) => {
    setLocalMachineName(value);

    const isValid =
      REGEX.commonNameFieldRegex.test(value) && REGEX.emojiRegex.test(value);
    let errorMessage: string | null = null;
    if (isValid) {
      errorMessage = null;
    } else if (!REGEX.commonNameFieldRegex.test(value)) {
      errorMessage = t("invalidCharacters");
    } else if (!REGEX.emojiRegex.test(value)) {
      errorMessage = t("emojisarenotallowed");
    }
    setMachineNumberError(errorMessage);

    onErrorChangeRef.current(
      cardId,
      "machineNumberError",
      errorMessage !== null,
    );
  };

  const handleMakerOtherChange = (value: string) => {
    setLocalCustomMachineManufacturerName(value);

    const isValid =
      REGEX.commonNameFieldRegex.test(value) && REGEX.emojiRegex.test(value);
    let errorMessage: string | null = null;
    if (isValid) {
      errorMessage = null;
    } else if (!REGEX.commonNameFieldRegex.test(value)) {
      errorMessage = t("invalidCharacters");
    } else if (!REGEX.emojiRegex.test(value)) {
      errorMessage = t("emojisarenotallowed");
    }
    setMakerOtherError(errorMessage);
    onErrorChangeRef.current(cardId, "makerOtherError", errorMessage !== null);
  };

  const handleMachineTypeOtherChange = (value: string) => {
    setLocalCustomTypeName(value);

    const isNonEmpty = value.trim() !== "";
    const isValid =
      isNonEmpty &&
      REGEX.commonNameFieldRegex.test(value) &&
      REGEX.emojiRegex.test(value);

    let errorMessage: string | null = null;
    if (isValid) {
      errorMessage = null;
    } else if (!REGEX.commonNameFieldRegex.test(value)) {
      errorMessage = t("invalidCharacters");
    } else if (!REGEX.emojiRegex.test(value)) {
      errorMessage = t("emojisarenotallowed");
    }
    setMachineTypeOtherError(errorMessage);
    onErrorChangeRef.current(
      cardId,
      "machineTypeOtherError",
      errorMessage !== null,
    );
  };

  const handleModelTypeChange = (value: string) => {
    const isKomatsuMachine = machineManufacturerId === KOMATSU_MANUFACTURER_ID;
    let processedValue = value;
    if (isKomatsuMachine) {
      processedValue = value
        .replace(/[‐‑–—‐‑‒–—⁃‑‒–—﹘―⎯⏤ーｰ─━－ー]/g, "-")
        .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (match) =>
          String.fromCharCode(match.charCodeAt(0) - 0xfee0),
        );
    }

    processedValue = processedValue
      .replace(
        /[^a-zA-Z0-9\s-_\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\uFF21-\uFF3A\uFF41-\uFF5A\uFF10-\uFF19\uFF0D]/g,
        "",
      )
      .replace(/^\s+/, "");

    if (isKomatsuMachine) {
      processedValue = processedValue.replace(/\s/g, "").toUpperCase();
    }

    setLocalModelAndType(processedValue);

    const isNonEmpty = processedValue.trim() !== "";
    const isValid =
      isNonEmpty &&
      REGEX.commonNameFieldRegex.test(processedValue) &&
      REGEX.emojiRegex.test(processedValue);

    let errorMessage: string | null = null;
    if (isValid) {
      errorMessage = null;
    } else if (!REGEX.commonNameFieldRegex.test(processedValue)) {
      errorMessage = t("invalidCharacters");
    } else if (!REGEX.emojiRegex.test(processedValue)) {
      errorMessage = t("emojisarenotallowed");
    }
    setModelTypeError(errorMessage);
    onErrorChangeRef.current(cardId, "modelTypeError", errorMessage !== null);
  };

  const handleSerialNumberChange = (value: string) => {
    let processedValue = value
      .replace(
        /[^a-zA-Z0-9\s-_\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\uFF21-\uFF3A\uFF41-\uFF5A\uFF10-\uFF19\uFF0D]/g,
        "",
      )
      .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (match) =>
        String.fromCharCode(match.charCodeAt(0) - 0xfee0),
      );

    if (machineManufacturerId === KOMATSU_MANUFACTURER_ID) {
      processedValue = processedValue.replace(/\s/g, "").toUpperCase();
    }

    setLocalSerialNumber(processedValue);

    let hasError = false;
    setSerialNoError(false);
    setForbiddenCharError(false);

    if (machineManufacturerId === KOMATSU_MANUFACTURER_ID) {
      const isValidSerial =
        machineTypeId === OTHER_MACHINE_TYPE_ID
          ? /^(NL\d{1,8}|(DB|DZ)[A-Z0-9]{1,8}|[ABCFHJKNY][0-9]{1,9}|[0-9]{1,10})$/.test(
              processedValue,
            )
          : /^(NL\d{1,6}|(DB|DZ)[A-Z0-9]{1,6}|[ABCFHJKNY][0-9]{1,7}|[0-9]{1,8})$/.test(
              processedValue,
            );
      setSerialNoError(!isValidSerial);
      hasError = !isValidSerial;
    } else {
      hasError = processedValue.length > 20;
      setSerialNoError(hasError);
    }

    onErrorChangeRef.current(
      cardId,
      "serialNoError",
      processedValue.length === 0 ? false : hasError,
    );
    onErrorChangeRef.current(
      cardId,
      "forbiddenCharError",
      processedValue.length === 0 ? false : hasError,
    );
  };

  const handlePreviewMedia = (mediaUrls: string[]) => {};

  useEffect(() => {
    let hasError = false;

    if (machineManufacturerId === KOMATSU_MANUFACTURER_ID) {
      hasError =
        machineTypeId === OTHER_MACHINE_TYPE_ID
          ? !/^(NL\d{1,8}|(DB|DZ)[A-Z0-9]{1,8}|[ABCFHJKNY][0-9]{1,9}|[0-9]{1,10})$/.test(
              localSerialNumber,
            )
          : !/^(NL\d{1,6}|(DB|DZ)[A-Z0-9]{1,6}|[ABCFHJKNY][0-9]{1,7}|[0-9]{1,8})$/.test(
              localSerialNumber,
            );
      handleModelTypeChange(modelAndType);
    } else {
      hasError = localSerialNumber.length > 20;
    }

    setSerialNoError(hasError);
    setForbiddenCharError(false);

    onErrorChangeRef.current(cardId, "serialNoError", hasError);
    onErrorChangeRef.current(cardId, "forbiddenCharError", false);
  }, [machineManufacturerId, machineTypeId, localSerialNumber, cardId]);

  const machineNameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen && machineNameInputRef.current) {
      machineNameInputRef.current.focus();
    }
  }, [isOpen]);

  const [isManufacturerTouched, setIsManufacturerTouched] = useState(false);
  const [isMachineTypeTouched, setIsMachineTypeTouched] = useState(false);
  const [isModelTypeTouched, setIsModelTypeTouched] = useState(false);
  const [isSerialTouched, setIsSerialTouched] = useState(false);

  const [cropOpen, setCropOpen] = useState(false);
  const [cropImage, setCropImage] = useState<string>("");
  const [cropFileName, setCropFileName] = useState<string>("");
  const [originalCropImage, setOriginalCropImage] = useState<string>("");
  const [originalCropFileName, setOriginalCropFileName] = useState<string>("");

  const getFileNameFromUrl = (url: string) => {
    const parsed = url.split("?")[0];
    const fileName = parsed.substring(parsed.lastIndexOf("/") + 1);
    return fileName || "image.jpeg";
  };

  const handleOpenEditCrop = async () => {
    try {
      if (originalCropImage) {
        setCropImage(originalCropImage);
        setCropFileName(originalCropFileName || "image.jpeg");
        setCropOpen(true);
        return;
      }

      if (!pictureUrl) return;

      const response = await fetch(pictureUrl);
      const blob = await response.blob();
      const fileName = getFileNameFromUrl(pictureUrl);
      const blobFile = new File([blob], fileName, {
        type: blob.type || "image/jpeg",
      });
      const blobUrl = URL.createObjectURL(blobFile);

      setOriginalCropImage(blobUrl);
      setOriginalCropFileName(blobFile.name);
      setCropImage(blobUrl);
      setCropFileName(blobFile.name);
      setCropOpen(true);
    } catch (error) {
      console.error("Failed to prepare image for editing:", error);
    } finally {
      setMenuAnchor(null);
    }
  };

  const handleCropComplete = (croppedFile: File) => {
    const previewUrl = URL.createObjectURL(croppedFile);

    setPictureFiles([croppedFile]);
    pictureUrlChange(previewUrl);

    setCropOpen(false);
  };

  const showRequiredBadge =
    (isManufacturerTouched && !machineManufacturerId) ||
    (isMachineTypeTouched && !machineTypeId) ||
    (isModelTypeTouched && !localModelAndType) ||
    (isSerialTouched && !localSerialNumber);

  return (
    <div ref={ref} style={{ ...styles.cardContainer }}>
      <div
        className="card-item-title flex"
        style={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <div className="flex align-items-center" style={{ gap: "15px" }}>
          <span
            className="txt-card txt-pre-wrap word-break"
            style={styles.itemName}
          >
            {localMachineName}
          </span>
        </div>

        <div className="flex align-items-center" style={{ gap: "10px" }}>
          {showRequiredBadge && (
            <span
              style={{
                color: "red",
                fontSize: "12px",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              {t("required")}*
            </span>
          )}

          <button
            disabled={isCopied}
            onClick={toggleOpen}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              padding: 0,
              color: "#cdcdcd",
            }}
          >
            {isOpen ? (
              <KeyboardArrowUp className="arrow-icon ml-10" />
            ) : (
              <KeyboardArrowDown className="arrow-icon ml-10" />
            )}
          </button>
        </div>
      </div>

      <div
        className="flex p-4 gap-6"
        style={{
          maxHeight: isOpen ? "1500px" : "0px",
          opacity: isOpen ? 1 : 0,
          overflow: "hidden",
          transition: "max-height 0.3s ease, opacity 0.3s ease",
          flexDirection: isSmallScreen ? "column" : "row",
          alignItems: isSmallScreen ? "center" : "flex-start",
          flexWrap: isSmallScreen ? "nowrap" : "wrap",
          gap: "20px",
          display: "flex",
        }}
      >
        <div style={{ flex: isSmallScreen ? "1" : "0 0 160px" }}>
          <FormControl fullWidth>
            <div
              className="image-box-container"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "0px",
              }}
            >
              <div
                className="image-box"
                style={{
                  width: "100%",
                  maxWidth: "150px",
                  height: "150px",
                  border: "1px dashed #ccc",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "4px",
                  backgroundColor: "transparent",
                  position: "relative",
                  marginTop: isSmallScreen ? "0px" : "60px",
                }}
              >
                {pictureUrl ? (
                  <>
                    <img
                      src={pictureUrl}
                      alt="Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                      onClick={(event) => setMenuAnchor(event.currentTarget)}
                    />
                    <Menu
                      anchorEl={menuAnchor}
                      open={Boolean(menuAnchor)}
                      onClose={() => setMenuAnchor(null)}
                    >
                      <MenuItem
                        onClick={() => {
                          setPreviewOpen(true);
                          setMenuAnchor(null);
                        }}
                      >
                        <Visibility
                          style={{ marginRight: "7px", color: "#4a4a4a" }}
                        />
                        {t("preview")}
                      </MenuItem>
                      <Divider style={{ margin: "4px 0" }} />
                      <MenuItem onClick={handleOpenEditCrop}>
                        <Edit
                          style={{ marginRight: "7px", color: "#4a4a4a" }}
                        />
                        {t("formAction.edit")}
                      </MenuItem>
                      <Divider style={{ margin: "4px 0" }} />
                      <MenuItem
                        onClick={() => {
                          pictureUrlChange(null);
                          setIsImageRemoved(true);
                          setOriginalCropImage("");
                          setOriginalCropFileName("");
                          setCropImage("");
                          setCropFileName("");
                          setMenuAnchor(null);
                        }}
                      >
                        <IconDeleteRow />

                        <span style={{ marginLeft: "7px" }}>
                          {t("machineInfo.removeimage")}
                        </span>
                      </MenuItem>
                    </Menu>
                  </>
                ) : fileError ? (
                  <span
                    style={{
                      color: "red",
                      fontSize: "12px",
                      textAlign: "center",
                      position: "absolute",
                    }}
                  >
                    {fileError}
                  </span>
                ) : (
                  <span style={{ color: "#ccc" }}>
                    {t("machineInfo.noimage")}
                  </span>
                )}
              </div>
            </div>

            <input
              id={`image-upload-${cardId}`}
              type="file"
              accept=".png, .jpg, .jpeg, .gif, .heic"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) return;

                const validImageTypes = [
                  "image/png",
                  "image/jpeg",
                  "image/jpg",
                  "image/gif",
                  "image/heic",
                ];

                const fileType = file.type;
                const fileExtension = file.name.split(".").pop()?.toLowerCase();

                const isValidFile = await isValidMediaFile(file);
                if (!isValidFile) {
                  setFileError(t("messages.corruptFileType"));
                  event.target.value = "";
                  return;
                }

                const isHeic =
                  fileType === "image/heic" ||
                  (!fileType && fileExtension === "heic");

                let finalFile = file;

                if (
                  validImageTypes.includes(fileType) ||
                  (!fileType && fileExtension === "heic")
                ) {
                  try {
                    if (isHeic) {
                      const convertedBlob = await heic2any({
                        blob: file,
                        toType: "image/jpeg",
                      });

                      finalFile = new File(
                        [convertedBlob as Blob],
                        file.name.replace(/\.heic$/i, ".jpeg"),
                        { type: "image/jpeg" },
                      );
                    }

                    const previewUrl = URL.createObjectURL(finalFile);

                    setCropImage(previewUrl);
                    setCropFileName(finalFile.name);
                    setOriginalCropImage(previewUrl);
                    setOriginalCropFileName(finalFile.name);
                    setCropOpen(true);

                    setFileError(null);
                  } catch (error) {
                    setFileError(t("machineInfo.errormessage"));
                  }
                } else {
                  setFileError(t("machineInfo.errormessage"));
                }

                event.target.value = "";
              }}
              style={{ display: "none" }}
            />

            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                component="label"
                className="btn btn-primary"
                htmlFor={`image-upload-${cardId}`}
                style={{
                  width: "160px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  backgroundColor: "#e2f2ff",
                  color: "#1e2c39",
                  marginBottom: "16px",
                  marginTop: "10px",
                }}
              >
                <UploadOutlined className="mr-5" />
                {t("machineInfo.uploadimage")}
              </Button>
            </div>
          </FormControl>
        </div>

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "5px",
          }}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
              <FormControl
                style={{
                  flex: isSmallScreen ? "1 1 100%" : "1 1 calc(50% - 7.5px)",
                }}
              >
                <TextField
                  label={`${t("machineInfo.customerMachineNumber")}`}
                  error={!!machineNumberError}
                  className="input-field"
                  value={localMachineName}
                  onChange={(e) => {
                    if (e.target.value.length <= 40) {
                      handleMachineNumberChange(e.target.value);
                    }
                  }}
                  onBlur={() => onMachineNameChange(localMachineName)}
                  inputRef={machineNameInputRef}
                  inputProps={{ maxLength: 40 }}
                  InputProps={{
                    endAdornment: machineNumberError && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                        }}
                      >
                        <IconInputError />
                      </div>
                    ),
                  }}
                />

                {machineNumberError ? (
                  <span className="error-text fs-12 ml-15 txt-red">
                    {machineNumberError}
                  </span>
                ) : (
                  <span className="fs-12 ml-15 txt-gray-1"></span>
                )}
              </FormControl>

              <FormControl
                style={{
                  flex: isSmallScreen ? "1 1 100%" : "1 1 calc(50% - 7.5px)",
                  marginTop: "8px",
                }}
              >
                <TextField
                  select
                  label={`${t("machineInfo.maker")}*`}
                  value={machineManufacturerId || ""}
                  onChange={(event) => {
                    setIsManufacturerTouched(true);
                    const selectedId = event.target.value;
                    onMachineManufacturerIdChange(selectedId);
                  }}
                  onBlur={() => setIsManufacturerTouched(true)}
                  error={isManufacturerTouched && !machineManufacturerId}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                          width: 250,
                        },
                      },
                    },
                  }}
                >
                  {manufacturers.map((manufacturer) => (
                    <MenuItem
                      key={manufacturer.machine_manufacturer_id}
                      value={manufacturer.machine_manufacturer_id}
                    >
                      {manufacturer.machine_manufacturer_name}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>

              {machineManufacturerId === OTHER_MANUFACTURER_ID && (
                <FormControl
                  style={{
                    flex: isSmallScreen ? "1 1 100%" : "1 1 calc(50% - 7.5px)",
                  }}
                >
                  <TextField
                    label={`${t("machineInfo.makerOther")}`}
                    value={localCustomMachineManufacturerName}
                    onChange={(event) => {
                      if (event.target.value.length <= 20) {
                        handleMakerOtherChange(event.target.value);
                      }
                    }}
                    onBlur={() =>
                      oncustomMachineManufacturerNameChange(
                        localCustomMachineManufacturerName,
                      )
                    }
                    error={!!makerOtherError}
                    inputProps={{ maxLength: 20 }}
                    InputProps={{
                      endAdornment: makerOtherError && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <IconInputError />
                        </div>
                      ),
                    }}
                    className="input-field"
                  />

                  {makerOtherError ? (
                    <span className="error-text fs-12 ml-15 txt-red">
                      {makerOtherError}
                    </span>
                  ) : (
                    <span className="fs-12 ml-15 txt-gray-1"></span>
                  )}
                </FormControl>
              )}

              <FormControl
                style={{
                  flex: isSmallScreen ? "1 1 100%" : "1 1 calc(50% - 7.5px)",
                  marginTop: "8px",
                }}
              >
                <TextField
                  select
                  label={`${t("machineInfo.machineType")}*`}
                  value={machineTypeId || ""}
                  onChange={(event) => {
                    setIsMachineTypeTouched(true);
                    const selectedId = event.target.value;
                    onmachineTypeIdChange(selectedId);
                  }}
                  onBlur={() => setIsMachineTypeTouched(true)}
                  error={isMachineTypeTouched && !machineTypeId}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                          width: 250,
                        },
                      },
                    },
                  }}
                >
                  {machinetype.map((type) => (
                    <MenuItem
                      key={type.machine_type_id}
                      value={type.machine_type_id}
                    >
                      {type.type_name}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>

              {machineTypeId === OTHER_MACHINE_TYPE_ID && (
                <FormControl
                  style={{
                    flex: isSmallScreen ? "1 1 100%" : "1 1 calc(50% - 7.5px)",
                  }}
                >
                  <TextField
                    label={`${t("machineInfo.machineTypeOther")}`}
                    value={localCustomTypeName}
                    onChange={(event) => {
                      if (event.target.value.length <= 40) {
                        handleMachineTypeOtherChange(event.target.value);
                      }
                    }}
                    onBlur={() => oncustomTypeNameChange(localCustomTypeName)}
                    fullWidth
                    error={!!machineTypeOtherError}
                    inputProps={{ maxLength: 40 }}
                    InputProps={{
                      endAdornment: machineTypeOtherError && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <IconInputError />
                        </div>
                      ),
                    }}
                    className="input-field"
                  />

                  {machineTypeOtherError ? (
                    <span className="error-text fs-12 ml-15 txt-red">
                      {machineTypeOtherError}
                    </span>
                  ) : (
                    <span className="fs-12 ml-15 txt-gray-1"></span>
                  )}
                </FormControl>
              )}

              <FormControl
                style={{
                  flex: isSmallScreen ? "1 1 100%" : "1 1 calc(50% - 7.5px)",
                }}
              >
                <TextField
                  label={`${t("machineInfo.modelType")}*`}
                  error={
                    isModelTypeTouched &&
                    (!localModelAndType || !!modelTypeError)
                  }
                  value={localModelAndType}
                  onChange={(event) => {
                    const inputValue = event.target.value;
                    if (inputValue.length <= 40) {
                      setIsModelTypeTouched(true);
                      handleModelTypeChange(inputValue);
                    }
                  }}
                  onBlur={() => {
                    setIsModelTypeTouched(true);
                    onmodelAndTypeChange(localModelAndType);
                  }}
                  onPaste={(event) => {
                    event.preventDefault();
                    let pastedText = event.clipboardData.getData("text");
                    handleModelTypeChange(pastedText);
                  }}
                  helperText={
                    isModelTypeTouched && modelTypeError ? modelTypeError : ""
                  }
                  inputProps={{ maxLength: 40 }}
                  InputProps={{
                    endAdornment: isModelTypeTouched &&
                      (!localModelAndType || modelTypeError) && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <IconInputError />
                        </div>
                      ),
                  }}
                  className="input-field"
                />
              </FormControl>

              <FormControl
                style={{
                  flex: isSmallScreen ? "1 1 100%" : "1 1 calc(50% - 7.5px)",
                }}
              >
                <TextField
                  label={`${t("machineInfo.serialNo")}*`}
                  error={
                    isSerialTouched &&
                    (!localSerialNumber ||
                      serialNoError ||
                      forbiddenCharError ||
                      (machineManufacturerId === KOMATSU_MANUFACTURER_ID &&
                        (machineTypeId === OTHER_MACHINE_TYPE_ID
                          ? !/^(NL\d{1,8}|(DB|DZ)[A-Z0-9]{1,8}|[ABCFHJKNY][0-9]{1,9}|[0-9]{1,10})$/.test(
                              localSerialNumber,
                            )
                          : !/^(NL\d{1,6}|(DB|DZ)[A-Z0-9]{1,6}|[ABCFHJKNY][0-9]{1,7}|[0-9]{1,8})$/.test(
                              localSerialNumber,
                            ))))
                  }
                  value={localSerialNumber}
                  onChange={(event) => {
                    setIsSerialTouched(true);
                    handleSerialNumberChange(event.target.value);
                  }}
                  onBlur={() => {
                    setIsSerialTouched(true);
                    onserialNumberChange(localSerialNumber);
                  }}
                  onKeyDown={(event) => {
                    const allowedKeys = /^[A-Za-z0-9- ０-９Ａ-Ｚａ-ｚ]$/;
                    const navigationKeys = [
                      "ArrowLeft",
                      "ArrowRight",
                      "Tab",
                      "Enter",
                    ];

                    if (
                      !allowedKeys.test(event.key.toUpperCase()) &&
                      event.key !== "Backspace" &&
                      event.key !== "Delete" &&
                      !navigationKeys.includes(event.key)
                    ) {
                      event.preventDefault();
                    }
                  }}
                  onPaste={(event) => {
                    event.preventDefault();
                    let pastedText = event.clipboardData.getData("text");
                    pastedText =
                      machineManufacturerId === KOMATSU_MANUFACTURER_ID
                        ? pastedText.toUpperCase()
                        : pastedText;
                    const allowedChars =
                      machineManufacturerId === KOMATSU_MANUFACTURER_ID
                        ? /^[A-Z0-9０-９Ａ-Ｚａ-ｚ]$/
                        : /^[A-Za-z0-9- ０-９Ａ-Ｚａ-ｚ]$/;
                    const sanitizedText = pastedText
                      .split("")
                      .filter((c) => allowedChars.test(c))
                      .join("");
                    handleSerialNumberChange(sanitizedText);
                  }}
                  helperText={
                    isSerialTouched &&
                    (forbiddenCharError ? (
                      <span
                        style={{
                          color: "red",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {t("machineInfo.serialNoTooLong")}
                      </span>
                    ) : machineManufacturerId === KOMATSU_MANUFACTURER_ID &&
                      localSerialNumber &&
                      (machineTypeId === OTHER_MACHINE_TYPE_ID
                        ? !/^(NL\d{1,8}|(DB|DZ)[A-Z0-9]{1,8}|[ABCFHJKNY][0-9]{1,9}|[0-9]{1,10})$/.test(
                            localSerialNumber,
                          )
                        : !/^(NL\d{1,6}|(DB|DZ)[A-Z0-9]{1,6}|[ABCFHJKNY][0-9]{1,7}|[0-9]{1,8})$/.test(
                            localSerialNumber,
                          )) ? (
                      <span
                        style={{
                          color: "red",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {t("machineInfo.serialNoTooLong")}
                      </span>
                    ) : localSerialNumber.length > 20 ? (
                      <span
                        style={{
                          color: "red",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {t("machineInfo.serialNoTooLong")}
                      </span>
                    ) : null)
                  }
                  InputProps={{
                    endAdornment: isSerialTouched &&
                      (!localSerialNumber ||
                        serialNoError ||
                        forbiddenCharError ||
                        (machineManufacturerId === KOMATSU_MANUFACTURER_ID &&
                          (machineTypeId === OTHER_MACHINE_TYPE_ID
                            ? !/^(NL\d{1,8}|(DB|DZ)[A-Z0-9]{1,8}|[ABCFHJKNY][0-9]{1,9}|[0-9]{1,10})$/.test(
                                localSerialNumber,
                              )
                            : !/^(NL\d{1,6}|(DB|DZ)[A-Z0-9]{1,6}|[ABCFHJKNY][0-9]{1,7}|[0-9]{1,8})$/.test(
                                localSerialNumber,
                              )))) && <IconInputError />,
                  }}
                  className="input-field"
                />
              </FormControl>
            </div>
          </div>

          <div
            className="wrapper-text-field mt-8"
            style={{ marginBottom: "0px" }}
          >
            <MediaUploadMachine
              key={refreshKey}
              id={cardId}
              setFileCount={setFileCount}
              medias={serialNumberPlatePictureUrl}
              setMedias={(files: File[]) => {
                const existingFiles = serialNumberPlatePictureUrl.map(
                  (file: any) =>
                    file.filePath || file.mediaUrl || file.thumbnailUrl,
                );
                const newFiles = files.map(
                  (file: any) => file.filePath || file.mediaUrl,
                );
                const removedFiles = existingFiles.filter(
                  (mediaUrl: string) => !newFiles.includes(mediaUrl),
                );
                if (removedFiles.length > 0) {
                  setRemovedMediaUrls((prev: any) => [
                    ...prev,
                    ...removedFiles,
                  ]);
                }
                onserialNumberPlatePictureUrlChange(files);
                setFileCount(files.length);
              }}
              onPreviewMedia={(mediaUrls: string[]) => {
                handlePreviewMedia(mediaUrls);
              }}
              typeFile={TYPE_FILE_UPLOAD.image}
              uploadDescription={uploadDescription}
            />
          </div>

          <div className="flex-centered justify-content-end mt-24">
            <Button
              className="btn-no-border"
              onClick={() => setIsOpenDeleteDialog(true)}
            >
              <IconDelete />
              <span className="txt-red ml-10 fs-14">
                {t("formAction.delete")}
              </span>
            </Button>
          </div>
        </div>
      </div>

      <DataDialog
        disableBackdropClick={false}
        disableEscapeKeyDown={false}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        action={
          <IconButton
            aria-label="close preview"
            onClick={() => setPreviewOpen(false)}
          >
            <Close />
          </IconButton>
        }
        maxWidth="md"
        fullWidth
      >
        <DialogContent
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
            backgroundColor: "transparent",
          }}
        >
          <img
            src={pictureUrl || undefined}
            alt="Full Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
            }}
          />
        </DialogContent>
      </DataDialog>

      <ImageCropDialog
        open={cropOpen}
        image={cropImage}
        fileName={cropFileName}
        onClose={() => setCropOpen(false)}
        onComplete={handleCropComplete}
      />

      <ConfirmDialog
        open={isOpenDeleteDialog}
        setOpen={setIsOpenDeleteDialog}
        {...confirmProps}
      />
    </div>
  );
};
const styles = {
  cardContainer: {
    borderRadius: "0px",
    padding: "16px",
    backgroundColor: "#f5f6f9",
    maxWidth: "100%",
    overflow: "hidden",
    borderBottom: "1px solid #eeeeee",
  },
  itemName: {
    fontSize: "16px",
  },
};

export default MachineCard;
